import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IoMdClose } from 'react-icons/io';

const ImageCropModal = ({ isOpen, onClose, image, onCropComplete }) => {
    const [crop, setCrop] = useState({
        unit: '%',
        width: 50,
        height: 50,
        x: 0,
        y: 0,
        aspect: 1,
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const getCroppedImg = async () => {
        try {
            setIsLoading(true);
            setUploadProgress(0);
            const image = imgRef.current;
            const canvas = document.createElement('canvas');
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            canvas.width = completedCrop.width;
            canvas.height = completedCrop.height;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(
                image,
                completedCrop.x * scaleX,
                completedCrop.y * scaleY,
                completedCrop.width * scaleX,
                completedCrop.height * scaleY,
                0,
                0,
                completedCrop.width,
                completedCrop.height
            );

            // Convert canvas to blob
            const blob = await new Promise((resolve) => {
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 1);
            });

            await onCropComplete(blob, (progress) => {
                setUploadProgress(progress);
            });
            onClose();
        } catch (error) {
            console.error('Error cropping image:', error);
        } finally {
            setIsLoading(false);
            setUploadProgress(0);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 rounded-full p-1 hover:bg-gray-100 transition-colors"
                    >
                        <IoMdClose size={20} className="text-gray-500" />
                    </button>

                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Crop Image</h2>

                    <div className="flex flex-col items-center gap-4">
                        <ReactCrop
                            crop={crop}
                            onChange={c => setCrop(c)}
                            onComplete={c => setCompletedCrop(c)}
                            aspect={1}
                            circularCrop
                        >
                            <img
                                ref={imgRef}
                                src={image}
                                alt="Crop me"
                                className="max-h-[60vh] object-contain"
                            />
                        </ReactCrop>

                        {isLoading && (
                            <div className="w-full">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-darkGreen h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <p className="text-sm text-gray-500 mt-1 text-center">
                                    {uploadProgress < 100
                                        ? `Uploading: ${Math.round(uploadProgress)}%`
                                        : 'Processing...'
                                    }
                                </p>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-4 w-full">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={getCroppedImg}
                                className="px-6 py-2 rounded-lg bg-darkGreen text-white hover:opacity-90"
                                disabled={!completedCrop}
                            >
                                Upload
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal; 